import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { getIn, useFormikContext } from 'formik';
import { Grid, Button } from '@material-ui/core';
import { FarmRiskType } from '@ourbranch/lookups';

import Label from 'core/components/label/label';
import AddCircleIcon from 'core/assets/svg/ic-add.svg?react';
import { FO6Scheduled } from '../risks/risk-types/FO6-scheduled';
import { FO6Unscheduled } from '../risks/risk-types/FO6-unscheduled';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useDisabledState } from 'common/disabled-context';

import useStyles from './farming-equipment.styles';

const defaultScheduledFarmPropertyRisk = {
  type: FarmRiskType.Fo6Scheduled,
  description: 'Scheduled farm property',
  adjustmentFactor: 0,
  coverages: {
    coverageF: [],
    expandedDebrisRemoval: false,
    hasSnowWeightCoverage: false,
    deductibleEarthquake: 0.15
  }
};

const defaultUnscheduledFarmPropertyRisk = {
  type: FarmRiskType.Fo6Unscheduled,
  description: 'Unscheduled farm property',
  adjustmentFactor: 0,
  coverages: {
    coverageG: 5000,
    deductible: 1000,
    deductibleEarthquake: 0.05,
    hasSnowWeightCoverage: false
  }
};

const FarmingEquipment = observer(({ fieldName }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();
  const { setActiveForm, activeForm } = useDisabledState();

  const risks = useMemo(() => getIn(values, `${fieldName}.risks`) || [], [values, fieldName]);

  const fo6Scheduled = risks.find((risk) => risk.type === 'FO6Scheduled');
  const fo6Unscheduled = risks.find((risk) => risk.type === 'FO6Unscheduled');

  // Calculate predictable risk indexes
  const fo6ScheduledIndex = risks.findIndex((risk) => risk.type === 'FO6Scheduled');
  const fo6UnscheduledIndex = risks.findIndex((risk) => risk.type === 'FO6Unscheduled');

  const total = Number(!!fo6Scheduled) + Number(!!fo6Unscheduled);

  const handleAddProperty = ({ risks, riskType }) => {
    const newRisks = [
      ...risks,
      {
        ...(riskType === 'FO6Scheduled' ? defaultScheduledFarmPropertyRisk : defaultUnscheduledFarmPropertyRisk)
      }
    ];
    setFieldValue(`${fieldName}.risks`, newRisks);
    const newRiskIndex = newRisks.length - 1;

    setActiveForm(`${fieldName}.risks[${newRiskIndex}]`);
  };

  return (
    <Grid container className={classes.mainContainer}>
      <Grid container justifyContent="space-between" className={classes.titleContainer}>
        <Label type="sectionSubtitle">Farming scheduled items</Label>
        <Grid item className={classes.rightContainer}>
          <Label type="body4" className={classes.rightLabel}>
            Total
          </Label>
          <Label type="body4">{total}</Label>
        </Grid>
      </Grid>
      <Grid container>
        {fo6Scheduled ? (
          <FO6Scheduled
            fieldName={fieldName}
            riskIndex={fo6ScheduledIndex}
            riskIdString={`${fieldName}.FO6Scheduled`}
          />
        ) : (
          <Grid className={classes.addContainer}>
            <p className={classes.addLabel}>Add scheduled farm property</p>
            <Button
              className={classes.addButton}
              onClick={() => handleAddProperty({ risks, riskType: 'FO6Scheduled' })}
              aria-label="Add scheduled farm property"
              disabled={activeForm}
            >
              <AddCircleIcon />
            </Button>
          </Grid>
        )}
        {fo6Unscheduled ? (
          <FO6Unscheduled
            fieldName={fieldName}
            riskIndex={fo6UnscheduledIndex}
            riskIdString={`${fieldName}.FO6Unscheduled`}
          />
        ) : (
          <Grid className={classes.addContainer}>
            <p className={classes.addLabel}>Add unscheduled farm property</p>
            <Button
              className={classes.addButton}
              onClick={() => handleAddProperty({ risks, riskType: 'FO6Unscheduled' })}
              aria-label="Add unscheduled farm property"
              disabled={activeForm}
            >
              <AddCircleIcon />
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
});

FarmingEquipment.propTypes = {
  fieldName: PropTypes.string.isRequired
};

export default FarmingEquipment;
